import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { View, FlatList, Dimensions } from "react-native";
import { useState, useRef, useEffect } from "react";
import { VDBIcons } from "@utils/VDBIcons";
import styled from "styled-components/native";
import React from "react";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import { VDBText } from "@components/atoms";
import { getBoxShadow } from "@utils/product/productValidators";
import { css } from "styled-components/native";
import { ProductCategory } from "@utils/constants";
import { getProductThemeFromType } from "@components/common/Header/utils";
import { OptionValue } from "@src/types/DiamondDetail";
import { DefaultTheme } from "styled-components/native";

export type DropDownProps = {
  title?: string;
  options: OptionValue[] | string[];
  onSelect: (value: string, item: OptionValue) => void;
  defaultValue?: string;
  productType: ProductCategory;
  showError?: boolean;
  isSubOptions?: boolean;
  height?: number;
};

const DropDown = ({
  options,
  defaultValue,
  onSelect,
  title,
  productType,
  showError,
  isSubOptions = false,
}: DropDownProps) => {
  const { theme, typography } = useScreenOptionsHooks();
  const productThemeType = getProductThemeFromType(productType);
  const triggerRef = useRef<View>(null);
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const [layout, setLayout] = useState(null);

  useEffect(() => {
    if (selectedValue !== defaultValue) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue]);

  const handleSelect = (value: string, item?: OptionValue | string) => {
    onSelect(value, item as OptionValue);
    setSelectedValue(value);
    setDropdownVisible(false);
  };

  const openDropdown = () => {
    if (!isDropdownVisible) {
      setDropdownPosition({
        top:
          Math.floor((layout as any)?.y) + Math.floor((layout as any)?.height),
        left: Math.floor((layout as any)?.x),
        width: Math.floor((layout as any)?.width),
      });
      setDropdownVisible(true);
    } else {
      setDropdownVisible(false);
    }
  };

  return (
    <Container>
      <TriggerButton
        onPress={openDropdown}
        theme={theme}
        isDropdownVisible={isDropdownVisible}
        productThemeType={productThemeType}
        showError={showError}
      >
        <View
          ref={triggerRef}
          style={{
            flexDirection: "column",
            gap: 4,
            flexShrink: 1,
            flexGrow: 1,
          }}
          onLayout={(event) => {
            setLayout(event?.nativeEvent?.layout as any);
          }}
        >
          {title && (
            <VDBText style={{ padding: 0, margin: 0 }}>{title}</VDBText>
          )}
          <VDBText
            color={selectedValue ? theme["core-05"] : theme["core-06"]}
            type={selectedValue ? typography["Body-M"] : typography["Body-R"]}
          >
            {(isSubOptions && options.includes(selectedValue as any)
              ? selectedValue
              : ((
                  options.find(
                    (option: any) => option.value === selectedValue
                  ) as OptionValue
                )?.label as string)) || "Select option"}
          </VDBText>
        </View>
        <VDBIcon
          size={24}
          color={theme["main-01"]}
          variant={IconsVariant.VDBCustom}
          name={
            isDropdownVisible
              ? VDBIcons.FILLED_CIRCLE_UP
              : VDBIcons.FILLED_CIRCLE_DOWN
          }
        />
      </TriggerButton>
      <View style={{ flex: 1 }}>
        {isDropdownVisible && dropdownPosition && (
          <DropdownContainer
            isDropdownVisible={isDropdownVisible}
            theme={theme}
            maxHeight={Dimensions.get("window").height * 0.4}
            id="dropdown"
            productThemeType={productThemeType}
          >
            <FlatList
              nestedScrollEnabled
              data={options as any}
              keyExtractor={(item, index) => `${item.value}-${index}`}
              renderItem={({ item, index }) => {
                return (
                  <>
                    <OptionButton
                      onPress={() =>
                        handleSelect(isSubOptions ? item : item.label, item)
                      }
                      isSelected={item.value === selectedValue}
                      isDisabled={item.isDisabled || false}
                      isHovered={hoveredIndex === index}
                      onHoverIn={() => {
                        setHoveredIndex(index);
                      }}
                      onHoverOut={() => {
                        setHoveredIndex(null);
                      }}
                    >
                      <VDBText
                        color={theme["core-06"]}
                        type={typography["Body-R"]}
                      >
                        {isSubOptions ? item : item.label}
                      </VDBText>
                    </OptionButton>
                  </>
                );
              }}
            />
          </DropdownContainer>
        )}
      </View>
    </Container>
  );
};

const Container = styled.View``;

const TriggerButton = styled.TouchableOpacity<{
  isDropdownVisible: boolean;
  productThemeType: string;
  showError: boolean;
}>`
  height: 56px;
  padding: 10px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-color: ${({
    theme,
    isDropdownVisible,
    productThemeType,
    showError,
  }: {
    theme: DefaultTheme;
    isDropdownVisible: boolean;
    productThemeType: string;
    showError: boolean;
  }) =>
    showError
      ? theme["error-01"]
      : isDropdownVisible
      ? theme[`${productThemeType}-04` as keyof DefaultTheme]
      : theme["core-04"]};
  border-width: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  ${({ isDropdownVisible }: { isDropdownVisible: boolean }) =>
    isDropdownVisible
      ? css`
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
          border-bottom-color: ${({ theme }) => theme["core-03"]};
        `
      : css`
          border-radius: 12px;
        `}
`;

const DropdownContainer = styled.View<{
  maxHeight: number;
  isDropdownVisible: boolean;
  productThemeType: string;
}>`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  max-height: ${(props: { maxHeight: number }) => props.maxHeight}px;
  overflow: hidden;
  z-index: 1000;
  width: 100%;
  border-color: ${({
    theme,
    isDropdownVisible,
    productThemeType,
  }: {
    theme: DefaultTheme;
    isDropdownVisible: boolean;
    productThemeType: string;
  }) =>
    isDropdownVisible
      ? theme[`${productThemeType}-04` as keyof DefaultTheme]
      : theme["core-04"]};
  border-width: 1px;
  box-shadow: ${({ theme }: { theme: DefaultTheme }) =>
    getBoxShadow(theme.Shadows.Gentle)};
  ${({ isDropdownVisible }: { isDropdownVisible: boolean }) =>
    isDropdownVisible &&
    css`
      border-top-color: ${({ theme }) => theme.transparent};
    `}
`;

const OptionButton = styled.Pressable<{
  isSelected: boolean;
  isDisabled: boolean;
  isHovered: boolean;
}>`
  padding: 8px;
  background-color: ${(props: {
    isSelected: boolean;
    isHovered: boolean;
    theme: DefaultTheme;
  }) =>
    props.isSelected || props.isHovered
      ? props.theme["core-02"]
      : "transparent"};
  opacity: ${(props: { isDisabled: boolean }) => (props.isDisabled ? 0.5 : 1)};
  height: 44px;
  justify-content: center;
`;

export default DropDown;
